import chartTheme from "@components/Charts/chartTheme";
import { ClientContext } from "@context/Client/ClientContext";
import { BarChart, BarChartTooltip, LineChart } from "@dive.tech/ui-graphs";
import LineChartTooltip from "@dive.tech/ui-graphs/Tooltips/LineChartTooltip";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { ClientModelInput, ModelScenarioOutput } from "@services/api/types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface KpiBudgetChartsProps {
  outputs: ModelScenarioOutput[];
  budget: ClientModelInput[];
  timePeriods: string[];
  media_investment: number;
  showBudget?: boolean;
  scenarioName?: string;
}

const KpiBudgetCharts = ({
  outputs,
  budget,
  timePeriods,
  media_investment,
  showBudget = false,
  scenarioName = ""
}: KpiBudgetChartsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentClient } = useContext(ClientContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(1000);

  const scName = useMemo(() => {
    return scenarioName || t("simulator.scenario.scenario");
  }, [scenarioName]);

  const barData = useMemo(() => {
    const inflowTotal = outputs.reduce((acc, elem) => elem.value + acc, 0);
    //const budgetTotal = budget.reduce((acc, elem) => elem.value + acc, 0);

    return [
      {
        name: currentClient.client.kpi_label,
        [scName]: inflowTotal
        // [t("simulator.scenario.budget")]: budgetTotal
      },
      {
        name: "Media Spend",
        [scName]: media_investment
        // [t("simulator.scenario.budget")]: 0
      }
    ];
  }, [
    t,
    scName,
    outputs,
    budget,
    media_investment,
    currentClient.client.kpi_label,
    scName
  ]);

  const lineData = useMemo(() => {
    const scenarioData = timePeriods.map((timePeriod) => {
      return {
        x: timePeriod,
        y: outputs.find((ouput) => ouput.time_period === timePeriod)?.value || 0
      };
    });

    /* const budgetData = timePeriods.map((timePeriod) => {
      return {
        x: timePeriod,
        y: budget.find((ouput) => ouput.time_period === timePeriod)?.value || 0
      };
    }); */

    return [
      {
        id: scName,
        color: theme.palette.primary.main,
        data: scenarioData
      }
      /* {
        id: t("simulator.scenario.budget"),
        color: "#85E139",
        data: budgetData
      } */
    ];
  }, [t, scName, timePeriods, outputs, budget]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Card className="w-full mt-4">
      <CardContent>
        <Grid container spacing={3} justifyContent="center">
          <Grid
            size={{ xs: 12, sm: 12, md: 6 }}
            sx={{ justifyContent: "center" }}
          >
            <Typography variant="h6" component="div" className="pt-4 pl-6">
              {t("simulator.scenario.inflow_bar_title", {
                kpi: currentClient.client.kpi_label
              })}
            </Typography>
            <Box sx={{ height: "500px" }} ref={chartContainerRef}>
              <BarChart
                data={barData}
                responsiveBarProps={{
                  theme: chartTheme,
                  tooltip: (props: any) =>
                    BarChartTooltip({
                      color: props.color,
                      label: props.label,
                      value: props.value,
                      id: props.id,
                      index: props.index,
                      indexValue: props.indexValue,
                      data: props.data,
                      yAxis: { yType: "number", yDecimals: 0 },
                      formattedValue: props.formattedValue,
                      hidden: props.hidden
                    }),
                  layers: ["grid", "markers", "axes", "legends", "bars"],
                  enableGridX: false,
                  enableLabel: false,
                  groupMode: "grouped",
                  indexScale: { type: "band", round: true },
                  valueScale: { type: "linear", min: 0, max: "auto" },
                  indexBy: "name",
                  keys: [
                    scName
                    //t("simulator.scenario.budget")
                  ],
                  colors: [
                    theme.palette.primary.main,
                    theme.palette.customColors.positiveVariationColor
                  ],
                  margin: {
                    top: 70,
                    left: 90,
                    right: 20,
                    bottom: isMobile ? 80 : 50
                  },
                  width: chartWidth,
                  height: 500,
                  axisLeft: {
                    tickSize: 10,
                    legendOffset: -70,
                    legendPosition: "middle"
                    // format: (value) => `${Number(value) / 1_000}k`
                  },
                  axisBottom: {
                    tickRotation: isMobile ? -15 : 0,
                    tickPadding: 10,
                    legendPosition: "middle"
                  },
                  legends: [
                    {
                      dataFrom: "keys",
                      anchor: "top",
                      direction: "row",
                      toggleSerie: true,
                      translateX: isMobile ? 50 : 170,
                      translateY: -40,
                      itemsSpacing: 10,
                      itemWidth: 150,
                      itemHeight: 10,
                      itemDirection: "left-to-right",
                      itemOpacity: 1,
                      symbolSize: 15,
                      padding: {
                        bottom: 120
                      }
                    }
                  ]
                }}
              />
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 12, md: 6 }}
            sx={{ justifyContent: "center" }}
          >
            <Typography variant="h6" component="div" className="pt-4 pl-6">
              {t("simulator.scenario.inflow_line_title", {
                kpi: currentClient.client.kpi_label
              })}
            </Typography>
            <Box sx={{ height: "500px" }} ref={chartContainerRef}>
              <LineChart
                data={lineData}
                responsiveLineProps={{
                  tooltip: (props: any) =>
                    LineChartTooltip({
                      point: props.point,
                      withSerieId: false,
                      xAxis: { xType: "string", xDecimals: 0 },
                      yAxis: { yType: "number", yDecimals: 0 }
                    }),
                  xScale: undefined,
                  yScale: { type: "linear", min: "auto", max: "auto" },
                  theme: chartTheme,
                  margin: { top: 50, left: 60, right: 20, bottom: 50 },
                  width: chartWidth,
                  curve: "linear",
                  height: 500,
                  axisLeft: {
                    tickSize: 10
                    //format: (value) => `${Number(value) / 1_000}k`
                  },
                  axisBottom: {
                    tickRotation: isMobile ? -15 : 0,
                    tickPadding: 20,
                    legendPosition: "middle"
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default KpiBudgetCharts;
